import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  Box,
  Grid2,
  MenuItem,
  Select,
  Typography,
  useTheme,
  Skeleton,
} from "@mui/material";
import { IoIosArrowDown } from "react-icons/io";
import { useParams } from "react-router-dom";
import { useGetManpowerShortfallByCategoryMutation } from "store/slices/apis/manpowerDashboardSlice";
import { ReactComponent as ChevronDownIcon } from "assets/PwC_Funct_Icons_ChevronDown_Outline_Black_RGB.svg"

const ManpowerShortfall = () => {
  const { projectId } = useParams();
  const theme = useTheme();
  const [shortfallData, setShortfallData] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [fetchShortfall] = useGetManpowerShortfallByCategoryMutation();
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async (categoryFilter = null) => {
    setIsLoading(true);
    try {
      const requestBody =
        categoryFilter && categoryFilter !== "All" ? [categoryFilter] : [];

      const response = await fetchShortfall({
        projectId,
        categoryList: requestBody,
      }).unwrap();

      if (response?.status === "success" && Array.isArray(response?.data)) {
        setShortfallData(response?.data);

        // Create category list for dropdown (only once)
        if (!categoriesList.length || categoryFilter === null) {
          const allCategories = [];
          response.data.forEach((item) => {
            if (item.category && !allCategories.includes(item.category)) {
              allCategories.push(item.category);
            }
          });
          setCategoriesList(allCategories);
        }
      }
    } catch (error) {
      console.error("Error", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedCategory === "All" ? null : selectedCategory);
  }, [selectedCategory]);

  const chartOptions = {
    chart: {
      type: "column",
      height: 150,
    },
    title: {
      text: "",
      align: "left",
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        color: "#21325B",
      },
    },
    xAxis: {
      categories: shortfallData.map((item) => item.category),
      lineWidth: 1,
      lineColor: "#758195",
      tickmarkPlacement: "on",
      tickLength: 6,
      tickWidth: 1,
      tickColor: "#415385",
      gridLineWidth: 0,
      labels: {
        style: {
          fontWeight: 500,
          color: "#00000080",
          fontSize: "12px",
        },
      },
    },
    yAxis: {
      title: {
        text: "",
      },
      lineWidth: 1,
      min:0,
      lineColor: "#758195",
      tickLength: 6,
      tickWidth: 1,
      tickColor: "#415385",
      gridLineWidth: 1,
      labels: {
        format: "{value}",
        style: {
          color: "#464646",
          fontWeight: 500,
          lineHeight: "14px",
          letterSpacing: "-0.5px",
        },
      },
    },
    plotOptions: {
      column: {
        pointPadding: 0.04,
        groupPadding: 0.1,
        borderRadius: 5,
        pointWidth: 70,
      },
    },
    colors: ["#CE412D"],
    series: [
      {
        name: "Shortfall",
        data: shortfallData.map((item) =>
          item.shortfall != null ? Number(item.shortfall.toFixed(2)) : 0
        ),
         dataLabels: {
            enabled: true,
            verticalAlign: 'top',
            style: {
                fontSize: '13px',
                fontWeight: 600,
                color: '#FFFFFF',
                textOutline: 'none'
            }
        }
      },
    ],
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      pointFormat: "<b>{point.y}</b>",
    },
  };


  const CustomChevronIcon = (props) => (
              <ChevronDownIcon style={{ width: '16px', height: '16px' }} {...props} />
          );

  return (
    <Box sx={{ padding: 2, background: "#fff", borderRadius: 2, height: "246px" ,  border: "1px solid #2525252E",}}>
      <Grid2 container>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Typography
            variant="body"
            sx={{
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: "100%",
              letterSpacing: "-0.4px",
              color: `${theme?.customSettings?.themeColor}`,
            }}
          >
            Manpower Shortfall
          </Typography>
          {/* <Typography
            sx={{
              mt: "5px",
              color: "#5052588A",
              fontSize: "14px",
              mb: "20px",
              lineHeight: "100%",
              letterSpacing: "-0.4px",
            }}
          >
            Get a holistic view of how work is being prioritized
          </Typography> */}
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "end" }}>
            <Select
              // IconComponent={IoIosArrowDown}
              IconComponent={CustomChevronIcon}
              sx={{
                backgroundColor: "#E8E8E8",
                "& .MuiOutlinedInput-input": {
                  padding: "5px 15px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              {categoriesList.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid2>
      </Grid2>

      <Grid2 xs={12} md={12}>
        {isLoading ? (
          <Skeleton variant="rectangular" height={150} sx={{ mt: 2 }} />
        ) : shortfallData.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 110,
              mt: 2,
              color: "#9E9E9E",
              fontSize: "16px",
            }}
          >
            No data available to display the chart.
          </Box>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        )}
      </Grid2>
    </Box>
  );
};

export default ManpowerShortfall;
